/**
 * Doctor Schedule management.
 */
import React, { useState, useEffect } from 'react';
import { doctorAPI } from '../../services/api';
import Layout from '../../components/Layout';
import toast from 'react-hot-toast';

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const DoctorSchedule = () => {
  const [schedule, setSchedule] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await doctorAPI.getSchedule();
        const raw = res.data || {};
        // Normalize: backend stores is_available, UI uses is_available
        const normalized = {};
        days.forEach(d => {
          const day = raw[d] || {};
          normalized[d] = {
            is_available: day.is_available ?? day.available ?? false,
            start_time: day.start_time || '08:00',
            end_time: day.end_time || '17:00',
            max_patients: day.max_patients || 10,
          };
        });
        setSchedule(normalized);
      } catch {
        // Initialize defaults
        const defaults = {};
        days.forEach(d => { defaults[d] = { is_available: false, start_time: '08:00', end_time: '17:00', max_patients: 10 }; });
        setSchedule(defaults);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleDay = (day) => {
    setSchedule(prev => ({
      ...prev,
      [day]: { ...(prev[day] || { start_time: '08:00', end_time: '17:00', max_patients: 10 }), is_available: !(prev[day]?.is_available) }
    }));
  };

  const updateTime = (day, field, value) => {
    setSchedule(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await doctorAPI.updateSchedule(schedule);
      toast.success('Schedule updated successfully.');
    } catch {
      toast.error('Failed to save schedule.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout title="My Schedule" role="doctor" showBack>
      <div className="p-4 pb-24">
        <p className="text-sm text-gray-500 mb-4">Set your availability for patient appointments.</p>

        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="skeleton h-16 rounded-2xl" />)}
          </div>
        ) : (
          <div className="space-y-3">
            {days.map(day => {
              const dayData = schedule[day] || {};
              return (
                <div key={day} className={`card transition-all ${dayData.is_available ? 'border-accent-200 bg-accent-50/30' : 'opacity-60'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => toggleDay(day)}
                        className={`w-10 h-6 rounded-full transition-colors relative ${
                          dayData.is_available ? 'bg-accent-500' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                          dayData.is_available ? 'translate-x-4' : 'translate-x-0.5'
                        }`} />
                      </button>
                      <span className="font-medium text-gray-900">{day}</span>
                    </div>
                    {dayData.is_available && (
                      <span className="text-xs text-accent-600 font-medium">Available</span>
                    )}
                  </div>

                  {dayData.is_available && (
                    <div className="flex items-center gap-3 mt-3">
                      <div className="flex-1">
                        <label className="text-xs text-gray-500">Start</label>
                        <input
                          type="time"
                          value={dayData.start_time || '08:00'}
                          onChange={e => updateTime(day, 'start_time', e.target.value)}
                          className="input-field py-1.5 text-sm"
                        />
                      </div>
                      <span className="text-gray-400 mt-4">—</span>
                      <div className="flex-1">
                        <label className="text-xs text-gray-500">End</label>
                        <input
                          type="time"
                          value={dayData.end_time || '17:00'}
                          onChange={e => updateTime(day, 'end_time', e.target.value)}
                          className="input-field py-1.5 text-sm"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            <button onClick={handleSave} disabled={saving} className="w-full py-3 rounded-xl font-semibold text-white bg-accent-500 hover:bg-accent-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-2 mt-4">
              {saving && <div className="spinner w-5 h-5 border-2"></div>}
              Save Schedule
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DoctorSchedule;
